<template>
    <Card ref="nodeRef" class="workspaceNodeCard" :class="{nested: depth > 0}">
        <template #title>
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                    <Button
                        :icon="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
                        text
                        rounded
                        size="small"
                        @click="toggle"
                    />
                    <i class="pi text-gray-600" :class="depth === 0 ? 'pi-book' : 'pi-folder'"/>
                    <div class="flex items-center">
                        <div v-tooltip="node.name"
                            class="font-medium" :class="{
                            'truncate max-w-36': !isTrainee && !expanded && workspaceResult,
                            'truncate max-w-64': !isTrainee && expanded
                        }">{{ node.name }}</div>
                        <i
                            v-if="hasRunningJobs"
                            class="pi pi-spin pi-spinner running-job-indicator"
                            v-tooltip="'PAC job is running in this process'"
                        />
                        <div class="text-xs ml-1" v-if="!userCan(WORKSPACE_PERMISSION.EDIT_WORKSPACE, node)">(read-only)</div>
                    </div>
                    <div class="flex gap-2 items-center"
                         v-if="!isTrainee && expanded && userCan(WORKSPACE_PERMISSION.EDIT_WORKSPACE, node)">
                        <Button
                            icon="pi pi-plus"
                            label="Folder"
                            rounded
                            text
                            severity="contrast"
                            @click="addChild('workspace')"
                        />
                        <Button
                            icon="pi pi-plus"
                            label="Graph"
                            rounded
                            text
                            @click="addChild('graph')"
                        />
                        <Button
                            icon="pi pi-play"
                            label="Run all"
                            rounded
                            text
                            data-cy="run-all-graphs"
                            :disabled="allRunnableGraphIds.length === 0 || runSelectionLoading"
                            @click="runAllInFolder"
                        />
                        <ToggleSwitch v-model="selectionEnabled" data-cy="workspace-selection-toggle"
                                      @click="selectedMenu = node"/>
                        <span class="font-medium text-base">Enable selection</span>
                    </div>

                </div>
                <div v-if="!isTrainee" class="flex gap-2 items-center">
                    <div v-if="!expanded" class="flex gap-2 items-center">
                        <div v-for="(v, t) in workspaceResult" class="flex flex-col items-center"
                             v-tooltip="progressTooltip(t,v)">
                            <span v-if="v" class="text-surface-900 text-base">{{ _.startCase(t) }}</span>
                            <ProgressBar v-if="v" :progress="v"
                                         :color="progressColor(v)"
                                         />
                        </div>
                    </div>
                    <span class="text-surface-900 dark:text-surface-0 font-medium mr-2 text-base" v-if="depth === 0">
                        {{ graphCount }} graph{{ graphCount === 1 ? '' : 's' }}
                    </span>
                    <Button type="button" icon="pi pi-ellipsis-v" text rounded
                            data-cy="workspace-context-menu-button"
                            @click.prevent.stop="evt => ctxToggle(evt, node)"/>
                    <Menu ref="menu" :popup="true" :model="menuItems" data-cy="workspace-context-menu">
                        <template #item="{ item, props }">
                            <a v-bind="props.action" :data-cy="item.dataCy">
                                <span v-if="item.icon" :class="item.icon"/>
                                <span class="ml-2">{{ item.label }}</span>
                            </a>
                        </template>
                    </Menu>
                </div>
            </div>
        </template>

        <template #content>
            <TrainingFolderCreateDialog v-if="IS_TRAINING_INSTANCE" v-model="showTrainingFolderDialog" :workspaceId="node.id" />
            <TrainingResultDialog v-if="IS_TRAINING_INSTANCE" v-model="showTrainingResultsDialog" :workspace="node" />
            <WorkspaceDialog
                :key="'workspace-dialog-' + node.id"
                v-model="displayWorkspaceDialog"
                :edit-element="editNode"
                :parent="node"/>

            <CreateGraphWizard
                v-model:visible="displayAddGraph"
                :key="'create-graph-' + node.id"
                :parent="node"/>

            <WorkspacePermission
                :key="'spacePerm' + node.id"
                :workspace="node"
                v-if="selectedMenu === node && userCan(WORKSPACE_PERMISSION.MANAGE_PERMISSIONS, node)"
                v-model:visible="showWorkspacePermissions"/>

            <MoveToDialog
                :graphs="selectedChildren"
                v-if="displayMoveToDialog"
                v-model:visible="displayMoveToDialog"
                @moved="displayMoveToDialog = false; selectedChildren = []; selectAllValue = false"/>

            <Menubar
                v-if="selectionEnabled"
                :model="items"
                breakpoint="100px"
                data-cy="workspace-selection-menubar"
                class="!fixed bottom-4 inset-x-0 mx-auto w-fit !bg-black  rounded-xl py-2 flex gap-3 shadow-lg z-50"
            />

            <!-- using v-show so that expand can be triggered bottom up -->
            <div
                ref="childrenContainerRef"
                class="pl-1 flex flex-col gap-2 workspace-children"
                v-show="expanded"
                @dragover="onContainerDragOver"
            >
                <div
                    v-for="(child, idx) in orderedChildren"
                    :key="depth + '_' + child.id"
                    :draggable="canReorderGraph(child)"
                    :data-cy="isGraph(child) ? 'graph-order-item' : 'workspace-order-item'"
                    :data-cy-name="isGraph(child) ? child.name : null"
                    :class="dropIndicatorClass(child)"
                    @dragstart="event => onDragStart(event, child)"
                    @dragover="event => onDragOver(event, child)"
                    @drop="event => onDrop(event, child)"
                    @dragleave="() => onDragLeave(child)"
                    @dragend="onDragEnd"
                >
                    <TreeNodeCard
                        v-model:selected="selectedChildren"
                        :node="child"
                        :depth="depth + 1"
                        @remove="removeChild(child)"
                        @update="emit('update')"
                        :selected-space-id="selectedSpaceId"
                        @expand="expand"
                        @drag-handle-down="onDragHandleDown"
                        :last="idx === orderedChildren.length - 1"
                        :selection-enabled="selectionEnabled"
                        :reorder-enabled="canReorderGraph(child)"
                    />
                </div>
            </div>
        </template>
    </Card>
</template>

<script setup>
import {ref, computed, watch, defineAsyncComponent, onMounted, onBeforeUnmount, nextTick} from 'vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import {useToast} from 'primevue/usetoast';
import TreeNodeCard from './TreeNodeCard.vue';
import WorkspaceModel from "@/src/model/WorkspaceModel";
import {GRAPH_PERMISSION, WORKSPACE_PERMISSION} from "@/src/model/Permission";
import {useConfirm} from "primevue/useconfirm";
import WorkspaceDialog from "@/src/views/repo/WorkspaceDialog.vue";
import { IS_TRAINING_INSTANCE } from '@/src/utils/utils';
import TrainingFolderCreateDialog from './TrainingFolderCreateDialog.vue';
import TrainingResultDialog from './TrainingResultDialog.vue';
const MoveToDialog = defineAsyncComponent(() => import('../graph/MoveToDialog'));
const CreateGraphWizard = defineAsyncComponent(() => import("@/src/views/createGraph/CreateGraphWizard.vue"));
const WorkspacePermission = defineAsyncComponent(() => import("@/src/views/repo/WorkspacePermission.vue"));
import {progressTooltip, progressColor} from "@/src/utils/utils";

import {useStore} from 'vuex';
import _ from "lodash-es";
import GraphModel from "@/src/model/GraphModel";
import {OP, usePending} from "@/src/queries/pending";

const store = useStore();
const toast = useToast();

const selectionEnabled = ref(false);
const selectedChildren = ref([]);

watch(selectionEnabled, v => {
    if (!v) {
        selectedChildren.value = [];
    }
})

const selectAllValue = ref(false);

const onSelectAllToggle = (value) => {
    if (value) {
        selectedChildren.value = props.node.children.filter(c => c instanceof GraphModel && (canEditGraph(c) || canDeleteGraph(c)));
    } else {
        selectedChildren.value = [];
    }
}


const props = defineProps({
    node: WorkspaceModel,
    depth: Number,
    depthColor: String,
    selectedSpaceId: Number
});

const emit = defineEmits(['remove', 'update', 'expand']);

const expanded = ref(false);

watch(() => props.node.children?.length, (newLen, oldLen) => {
    if (newLen > (oldLen || 0)) {
        expanded.value = true;
    }
});

onMounted(() => {
    expandIfSelected(props.selectedSpaceId)
})

watch(() => props.selectedSpaceId, (selectedSpaceId) => {
    expandIfSelected(selectedSpaceId)
})

const expandIfSelected = (selectedSpaceId) => {
    if (props.node.id === selectedSpaceId) {
        expand()
    }
}

const nodeRef = ref(null);

const toggle = () => {
    expanded.value = !expanded.value;
};

const expand = () => {
    expanded.value = true
    store.dispatch('repoStore/loadGraphOrder', props.node.id)
    emit('expand')
    nextTick(() => {
        // scroll to the node if expanded
        if (!nodeRef.value) {
            return
        }
        nodeRef.value.$el.scrollIntoView({behavior: 'smooth', block: 'start'});
    })
}

const editNode = ref(null);

const addChild = (type) => {
    editNode.value = null
    if (type === 'workspace') {
        displayWorkspaceDialog.value = true
    } else {
        displayAddGraph.value = true;
    }
};

const removeChild = (child) => {
    props.node.children = props.node.children.filter(c => c.key !== child.key);
    emit('update');
};

const selectedMenu = computed({
    get() {
        return store.getters["repoStore/selectedMenu"]
    },
    set(value) {
        store.commit('repoStore/setSelectedMenu', value)
    }
})
const menu = ref()

const ctxToggle = (evt, ws) => {
    menu.value.toggle(evt)
    selectedMenu.value = ws;
}

watch(selectedMenu, m => {
    if (m !== props.node && menu.value && menu.value.visible) {
        menu.value.hide()
    }
    if (m !== props.node && selectionEnabled.value) {
        selectionEnabled.value = false
        resetSelection()
    }
})

const userCan = store.getters['userStore/userCan']
const runSelectionLoading = usePending(OP.RUN_JOBS)
const showWorkspacePermissions = ref(false)
const displayWorkspaceDialog = ref(false);
const displayAddGraph = ref(false)
const displayMoveToDialog = ref(false)

const nodeType = computed(() => {
    return props.node.parent ? 'Folder' : 'Process'
})

const showTrainingFolderDialog = ref(false)
const showTrainingResultsDialog = ref(false)

const menuItems = computed(() => {
    const ws = props.node
    let res = [
        {
            label: 'Create users training folder',
            icon: 'pi pi-briefcase',
            class: 'training-create-users-folder',
            visible: IS_TRAINING_INSTANCE && !props.node.parent,
            command: () => {
                showTrainingFolderDialog.value = true
            }
        },
        {
            label: 'Check training results',
            icon: 'pi pi-chart-bar',
            class: 'training-check-results',
            visible: IS_TRAINING_INSTANCE && !props.node.parent,
            command: () => {
                showTrainingResultsDialog.value = true
            }
        },
        {
            label: `Edit ${nodeType.value}`,
            icon: 'pi pi-pencil',
            dataCy: 'workspace-context-menu-item-edit',
            command: editSpace,
            visible: userCan(WORKSPACE_PERMISSION.EDIT_WORKSPACE, ws),
            class: 'rename-workspace'
        },
        {
            label: `Delete ${nodeType.value}`,
            icon: 'pi pi-trash',
            dataCy: 'workspace-context-menu-item-delete',
            command: deleteItem,
            visible: graphCount.value === 0 && userCan(WORKSPACE_PERMISSION.DELETE_WORKSPACE, ws),
            class: 'delete-workspace'
        },
        {
            label: `Set permissions for this ${nodeType.value.toLowerCase()}`,
            icon: 'pi pi-users',
            dataCy: 'workspace-context-menu-item-permissions',
            command: () => showWorkspacePermissions.value = true,
            visible: userCan(WORKSPACE_PERMISSION.MANAGE_PERMISSIONS, ws),
            class: 'permissions-workspace'
        },

    ].filter(item => item.visible)

    if (res.length > 0) {
        return res
    }

    return [
        {
            label: `This ${nodeType.value.toLowerCase()} has only view permission`,
            dataCy: 'workspace-context-menu-item-view-only',
            disabled: true
        }
    ]
})

const editSpace = () => {
    editNode.value = props.node
    displayWorkspaceDialog.value = true
}

const confirm = useConfirm();
const deleteItem = (event) => {
    confirm.require({
        group: 'deleteWorkspace',
        target: event.target,
        message: `Are you sure you want to delete this ${nodeType.value.toLowerCase()}?`,
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
            //callback to execute when user confirms the action
            store.dispatch('repoStore/deleteWorkspace', props.node)
        },
    });
}

const resetSelection = () => {
    selectedChildren.value = [];
    selectAllValue.value = false;
}

const deleteSelection = () => {
    confirm.require({
        group: 'deleteWorkspace',
        message: `Are you sure you want to delete selected graphs?`,
        icon: 'pi pi-exclamation-triangle',
        acceptProps: {rounded: true},
        acceptIcon: 'pi pi-check',
        rejectProps: {rounded: true},
        rejectIcon: 'pi pi-times',
        rejectClass: 'p-button-text',
        accept: async () => {
            //callback to execute when user confirms the action
            try {
                await store.dispatch('repoStore/deleteGraphs', selectedChildren.value.map(n => n.id))
                resetSelection()
            } catch (e) {
                toast.add({
                    severity: 'error',
                    summary: 'Delete failed',
                    detail: e?.response?.data?.error?.message || 'Could not delete the selected graphs.',
                    life: 5000
                })
                // the backend deletes one graph at a time, so a failure can leave a partial result
                resetSelection()
                store.dispatch('repoStore/loadWorkspaces')
            }
        },
    });
}

const runSelection = async () => {
    const graphIds = selectedChildren.value.map(graph => graph.id)
    await runGraphs(graphIds)
}

const runGraphs = async (graphIds) => {
    if (!graphIds || graphIds.length === 0) {
        return
    }

    const response = await store.dispatch('repoStore/runGraphs', graphIds)
    const startedRuns = response?.job_executions?.length || 0
    const skippedRuns = response?.skipped_graph_ids?.length || 0
    const skippedUnpublishedRuns = response?.skipped_unpublished_graph_ids?.length || 0
    const skippedRunningRuns = response?.skipped_running_graph_ids?.length || 0
    const skippedUnauthorizedRuns = response?.skipped_unauthorized_graph_ids?.length || 0

    if (startedRuns > 0) {
        toast.add({
            severity: 'success',
            summary: `Started ${startedRuns} run${startedRuns === 1 ? '' : 's'}`,
            life: 5000,
        })
    }

    if (skippedRuns > 0) {
        const details = []
        if (skippedUnpublishedRuns > 0) {
            details.push(`${skippedUnpublishedRuns} without a published version`)
        }
        if (skippedRunningRuns > 0) {
            details.push(`${skippedRunningRuns} already running`)
        }
        if (skippedUnauthorizedRuns > 0) {
            details.push(`${skippedUnauthorizedRuns} unauthorized`)
        }
        toast.add({
            severity: 'warn',
            summary: `Skipped ${skippedRuns} graph${skippedRuns === 1 ? '' : 's'}`,
            detail: details.length > 0 ? details.join(', ') : 'Only graphs with a published version can be run.',
            life: 6000,
        })
    }
}

const getWorkspaceGraphs = (workspace) => {
    const graphs = []
    for (const child of workspace.children || []) {
        if (child instanceof GraphModel) {
            graphs.push(child)
            continue
        }
        if (child instanceof WorkspaceModel) {
            graphs.push(...getWorkspaceGraphs(child))
        }
    }
    return graphs
}

const allRunnableGraphIds = computed(() => {
    const graphs = getWorkspaceGraphs(props.node)
    return graphs.filter(canEditGraph).map(graph => graph.id)
})

const hasRunningJobs = computed(() => {
    const graphs = getWorkspaceGraphs(props.node)
    return graphs.some(graph => !!graph.is_running)
})

const runAllInFolder = (event) => {
    const graphCount = allRunnableGraphIds.value.length
    if (graphCount === 0) {
        return
    }

    confirm.require({
        group: 'deleteWorkspace',
        target: event?.currentTarget,
        message: `Run all ${graphCount} graph${graphCount === 1 ? '' : 's'} in this folder?`,
        icon: 'pi pi-exclamation-triangle',
        acceptProps: {rounded: true},
        acceptIcon: 'pi pi-check',
        rejectProps: {rounded: true},
        rejectIcon: 'pi pi-times',
        rejectClass: 'p-button-text',
        accept: async () => {
            await runGraphs(allRunnableGraphIds.value)
        },
    })
}

const workspaceResult = computed(() => {
    const children = props.node.children.filter(c => !_.isEmpty(c.result))
    if (children.length === 0) {
        return null

    }
    const ad = _.reduce(children, ([num, den], c) => {
        return [num + c.result.adherence_ratio * c.result.n_cases, den + c.result.n_cases]
    }, [0, 0])
    const ad_ratio = ad[0] / ad[1]
    return {
        'digitalization': _.meanBy(children, 'result.digitalization'),
        'adherence_ratio': ad_ratio,
        'coverage': _.maxBy(children, 'result.coverage') ? _.maxBy(children, 'result.coverage')['result']['coverage'] : 0.00,
    }
})

const graphCount = computed(() => {
    // return props.node.children.filter(c => c instanceof GraphModel).length
    // recursively count graphs in children workspaces
    const countGraphs = (workspace) => {
        let count = 0;
        for (const child of workspace.children) {
            if (child instanceof GraphModel) {
                count += 1;
            } else if (child instanceof WorkspaceModel) {
                count += countGraphs(child);
            }
        }
        return count;
    };
    return countGraphs(props.node);
})

const canEditGraph = (graph) => {
    const isLocked = store.getters["graphStore/isLockedByAnotherUser"](graph)
    return !isLocked && (userCan(GRAPH_PERMISSION.EDIT_GRAPH, graph) ||
        userCan(WORKSPACE_PERMISSION.EDIT_ALL_GRAPHS, graph.parent))
}

const canDeleteGraph = (graph) => {
    const isLocked = store.getters["graphStore/isLockedByAnotherUser"](graph)
    return !isLocked && (userCan(GRAPH_PERMISSION.DELETE_GRAPH, graph) ||
        userCan(WORKSPACE_PERMISSION.DELETE_ALL_GRAPHS, graph.parent))
}

const graphOrder = computed(() => store.getters['repoStore/graphOrderByWorkspace'](props.node.id))

const isGraph = (child) => child instanceof GraphModel

const canReorderGraph = (child) => isGraph(child) && !selectionEnabled.value

const orderedChildren = computed(() => {
    const children = props.node.children || []
    const graphChildren = children.filter(isGraph)
    const order = graphOrder.value || []

    if (graphChildren.length < 2 || order.length === 0) {
        return children
    }

    const initialGraphIndex = new Map(graphChildren.map((child, idx) => [child.id, idx]))
    const orderedGraphs = [...graphChildren].sort((a, b) => {
        const aPos = order.indexOf(a.id)
        const bPos = order.indexOf(b.id)

        if (aPos === -1 && bPos === -1) {
            return initialGraphIndex.get(a.id) - initialGraphIndex.get(b.id)
        }
        if (aPos === -1) {
            return 1
        }
        if (bPos === -1) {
            return -1
        }
        return aPos - bPos
    })

    let graphIdx = 0
    return children.map((child) => {
        if (!isGraph(child)) {
            return child
        }
        const graph = orderedGraphs[graphIdx]
        graphIdx += 1
        return graph
    })
})

const draggedGraphId = ref(null)
const dragOverGraphId = ref(null)
const dragOverPosition = ref(null)
const dragHandleGraphId = ref(null)
const dragHandleArmedAt = ref(0)
const childrenContainerRef = ref(null)
const dragClientY = ref(0)
const autoScrollFrame = ref(null)
const autoScrollDirection = ref(0)
const autoScrollScope = ref('container')
const outerScrollTarget = ref(null)
const outerScrollAncestors = ref([])

const getScrollableAncestors = (element) => {
    const res = []
    let parent = element?.parentElement || null
    while (parent) {
        const style = getComputedStyle(parent)
        const overflowY = style.overflowY
        const canScrollY = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'
        if (canScrollY) {
            res.push(parent)
        }
        parent = parent.parentElement
    }
    return res
}

const scrollElementBy = (element, delta) => {
    if (!element) {
        return false
    }
    const maxScrollTop = element.scrollHeight - element.clientHeight
    if (maxScrollTop <= 0) {
        return false
    }
    const nextScrollTop = Math.min(maxScrollTop, Math.max(0, element.scrollTop + delta))
    if (nextScrollTop === element.scrollTop) {
        return false
    }
    element.scrollTop = nextScrollTop
    return true
}

const getViewportRect = (viewportElement) => {
    if (!viewportElement) {
        return {
            top: 0,
            bottom: window.innerHeight,
        }
    }
    return viewportElement.getBoundingClientRect()
}

const isListBoundaryVisible = (direction, viewportElement) => {
    const list = childrenContainerRef.value
    if (!list || !list.firstElementChild || !list.lastElementChild) {
        return false
    }

    const viewportRect = getViewportRect(viewportElement)
    const firstRect = list.firstElementChild.getBoundingClientRect()
    const lastRect = list.lastElementChild.getBoundingClientRect()
    const tolerance = 2

    if (direction < 0) {
        return firstRect.top >= viewportRect.top - tolerance
    }
    if (direction > 0) {
        return lastRect.bottom <= viewportRect.bottom + tolerance
    }
    return false
}

const stopAutoScroll = () => {
    autoScrollDirection.value = 0
    autoScrollScope.value = 'container'
    outerScrollTarget.value = null
    if (autoScrollFrame.value !== null) {
        cancelAnimationFrame(autoScrollFrame.value)
        autoScrollFrame.value = null
    }
}

const runAutoScroll = () => {
    const container = childrenContainerRef.value
    if (!container || autoScrollDirection.value === 0) {
        autoScrollFrame.value = null
        return
    }

    const rect = container.getBoundingClientRect()
    const topDistance = Math.max(0, dragClientY.value - rect.top)
    const bottomDistance = Math.max(0, rect.bottom - dragClientY.value)
    const edgeDistance = autoScrollDirection.value < 0 ? topDistance : bottomDistance
    const boost = Math.max(0, 56 - edgeDistance)
    const delta = autoScrollDirection.value * (4 + boost / 6)

    const outerContainer = outerScrollTarget.value
    let moved = false

    const scrollOuter = () => {
        const activeViewport = outerScrollTarget.value || null
        if (isListBoundaryVisible(autoScrollDirection.value, activeViewport)) {
            return false
        }

        for (const el of outerScrollAncestors.value) {
            if (el === container) {
                continue
            }
            if (isListBoundaryVisible(autoScrollDirection.value, el)) {
                continue
            }
            if (scrollElementBy(el, delta)) {
                outerScrollTarget.value = el
                return true
            }
        }
        if (outerContainer && outerContainer !== container) {
            if (isListBoundaryVisible(autoScrollDirection.value, outerContainer)) {
                return false
            }
            if (scrollElementBy(outerContainer, delta)) {
                return true
            }
        }
        if (isListBoundaryVisible(autoScrollDirection.value, null)) {
            return false
        }
        const nextWindowY = Math.max(0, window.scrollY + delta)
        if (nextWindowY !== window.scrollY) {
            window.scrollTo({ top: nextWindowY })
            return true
        }
        return false
    }

    const scrollContainer = () => {
        return scrollElementBy(container, delta)
    }

    if (autoScrollScope.value === 'outer') {
        moved = scrollOuter()
        if (!moved) {
            moved = scrollContainer()
        }
    } else {
        moved = scrollContainer()
        if (!moved) {
            moved = scrollOuter()
        }
    }

    if (!moved) {
        stopAutoScroll()
        return
    }

    autoScrollFrame.value = requestAnimationFrame(runAutoScroll)
}

const updateAutoScroll = (clientY) => {
    const container = childrenContainerRef.value
    if (!container || draggedGraphId.value === null) {
        stopAutoScroll()
        return
    }

    dragClientY.value = clientY

    const rect = container.getBoundingClientRect()
    const threshold = 56
    const outerThreshold = 48
    const outerContainer = outerScrollTarget.value
    const outerRect = outerContainer ? outerContainer.getBoundingClientRect() : { top: 0, bottom: window.innerHeight }
    const outerTop = outerRect.top
    const outerBottom = outerRect.bottom
    const nearOuterTop = clientY < outerTop + outerThreshold
    const nearOuterBottom = clientY > outerBottom - outerThreshold
    const nearContainerTop = clientY < rect.top + threshold
    const nearContainerBottom = clientY > rect.bottom - threshold
    let direction = 0
    if (nearContainerTop || nearOuterTop) {
        direction = -1
    } else if (nearContainerBottom || nearOuterBottom) {
        direction = 1
    }

    if (direction === 0) {
        stopAutoScroll()
        return
    }

    if (autoScrollDirection.value !== direction) {
        autoScrollDirection.value = direction
    }

    const shouldPreferOuter = (direction < 0 && nearOuterTop && !nearContainerTop) ||
        (direction > 0 && nearOuterBottom && !nearContainerBottom)
    autoScrollScope.value = shouldPreferOuter ? 'outer' : 'container'

    if (autoScrollFrame.value === null) {
        autoScrollFrame.value = requestAnimationFrame(runAutoScroll)
    }
}

const onContainerDragOver = (event) => {
    if (draggedGraphId.value === null) {
        return
    }
    event.preventDefault()
    updateAutoScroll(event.clientY)
}

const onGlobalDragOver = (event) => {
    if (draggedGraphId.value === null || !event.clientY) {
        return
    }
    updateAutoScroll(event.clientY)
}

const onDragHandleDown = (graphId) => {
    dragHandleGraphId.value = graphId
    dragHandleArmedAt.value = Date.now()
}

const onDragStart = (event, child) => {
    if (!canReorderGraph(child)) {
        return
    }

    const canStartDrag = dragHandleGraphId.value === child.id && Date.now() - dragHandleArmedAt.value < 1000
    dragHandleGraphId.value = null
    dragHandleArmedAt.value = 0
    if (!canStartDrag) {
        event.preventDefault()
        return
    }

    draggedGraphId.value = child.id
    outerScrollAncestors.value = getScrollableAncestors(childrenContainerRef.value)
    outerScrollTarget.value = outerScrollAncestors.value[0] || null
    window.addEventListener('dragover', onGlobalDragOver)
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', String(child.id))
}

const onDragOver = (event, child) => {
    if (!canReorderGraph(child) || draggedGraphId.value === null || draggedGraphId.value === child.id) {
        return
    }

    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    dragOverGraphId.value = child.id
    const rect = event.currentTarget.getBoundingClientRect()
    dragOverPosition.value = event.clientY < rect.top + rect.height / 2 ? 'before' : 'after'
    updateAutoScroll(event.clientY)
}

const onDragLeave = (child) => {
    if (dragOverGraphId.value === child.id) {
        dragOverGraphId.value = null
        dragOverPosition.value = null
    }
}

const dropIndicatorClass = (child) => {
    const isActive = dragOverGraphId.value === child.id && draggedGraphId.value !== child.id
    return {
        'graph-drop-before': isActive && dragOverPosition.value === 'before',
        'graph-drop-after': isActive && dragOverPosition.value === 'after',
    }
}

const onDrop = async (event, targetChild) => {
    event.preventDefault()

    if (!canReorderGraph(targetChild) || draggedGraphId.value === null || draggedGraphId.value === targetChild.id) {
        onDragEnd()
        return
    }

    const graphIds = orderedChildren.value.filter(isGraph).map(child => child.id)
    const sourceIdx = graphIds.indexOf(draggedGraphId.value)
    const targetIdx = graphIds.indexOf(targetChild.id)

    if (sourceIdx === -1 || targetIdx === -1 || sourceIdx === targetIdx) {
        onDragEnd()
        return
    }

    const nextOrder = [...graphIds]
    const [movedGraphId] = nextOrder.splice(sourceIdx, 1)
    const insertAfter = dragOverPosition.value === 'after'
    let insertIdx = targetIdx + (insertAfter ? 1 : 0)
    if (sourceIdx < insertIdx) {
        insertIdx -= 1
    }

    if (insertIdx === sourceIdx) {
        onDragEnd()
        return
    }

    nextOrder.splice(insertIdx, 0, movedGraphId)

    await store.dispatch('repoStore/updateGraphOrder', {
        workspaceId: props.node.id,
        order: nextOrder,
    })

    onDragEnd()
}

const onDragEnd = () => {
    window.removeEventListener('dragover', onGlobalDragOver)
    draggedGraphId.value = null
    dragOverGraphId.value = null
    dragOverPosition.value = null
    dragHandleGraphId.value = null
    dragHandleArmedAt.value = 0
    stopAutoScroll()
}

onBeforeUnmount(() => {
    window.removeEventListener('dragover', onGlobalDragOver)
})

watch(expanded, v => {
    if (!v) {
        // reset selection when collapsed
        resetSelection()
        selectionEnabled.value = false
    }
    if (v && props.depth === 0) {
        // load graph results when expanded at root level
        store.dispatch('repoStore/loadGraphResults', props.node.id)
    }
    if (v) {
        store.dispatch('repoStore/loadGraphOrder', props.node.id)
    }

})


const items = computed(() => [
    {
        label: props.node.name,
        disabled: true,
        class: 'max-w-40 floating-name',
        visible: true,
    },
    {
        label: selectAllValue.value ? 'Deselect All' : 'Select All',
        icon: selectAllValue.value ? 'pi pi-check-square' : 'pi pi-stop',
        class: selectAllValue.value ? 'workspace-selection-deselect-all' : 'workspace-selection-select-all',
        command: () => {
            selectAllValue.value = !selectAllValue.value;
            onSelectAllToggle(selectAllValue.value);
        },
        visible: true
    },
    {
        label: `Move ${selectedChildren.value.length} graph${selectedChildren.value.length === 1 ? '' : 's'}`,
        icon: 'pi pi-folder',
        command: () => {
            displayMoveToDialog.value = true
        },
        disabled: selectedChildren.value.length === 0,
        visible: _.every(selectedChildren.value, canEditGraph)
    },
    {
        label: `Run ${selectedChildren.value.length} graph${selectedChildren.value.length === 1 ? '' : 's'}`,
        icon: 'pi pi-play',
        command: runSelection,
        disabled: selectedChildren.value.length === 0 || runSelectionLoading.value,
        visible: _.every(selectedChildren.value, canEditGraph)
    },
    {
        label: `Delete ${selectedChildren.value.length} graph${selectedChildren.value.length === 1 ? '' : 's'}`,
        icon: 'pi pi-trash',
        command: deleteSelection,
        disabled: selectedChildren.value.length === 0,
        visible: _.every(selectedChildren.value, canDeleteGraph)
    }
].filter(item => item.visible)
);

const isTrainee = computed(() => store.getters['userStore/isTrainee'])

</script>
<style scoped lang="scss">
.workspaceNodeCard :deep(.p-card-body) {
    padding-right: 0 !important;
}

.workspaceNodeCard {
    box-shadow: -4px 0 8px rgba(0, 0, 0, 0.2);
    .nested {
        // less shadow for nested workspaces
        box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
    }
}

.workspaceNodeCard :deep(.p-menubar-item-label) {
    color: white !important;
}

.workspaceNodeCard :deep(.p-menubar-item-content:hover .p-menubar-item-label) {
    color: black !important;
}

.workspaceNodeCard :deep(.floating-name .p-menubar-item-label) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.graph-drop-before {
    position: relative;
    box-shadow: inset 0 0 0 2px var(--p-primary-color, #3b82f6);
    background: rgba(59, 130, 246, 0.12);
    border-radius: 0.5rem;
}

.graph-drop-after {
    position: relative;
    box-shadow: inset 0 0 0 2px var(--p-primary-color, #3b82f6);
    background: rgba(59, 130, 246, 0.12);
    border-radius: 0.5rem;
}

.graph-drop-before::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: -2px;
    height: 6px;
    border-radius: 999px;
    background: var(--p-primary-color, #3b82f6);
    pointer-events: none;
    z-index: 3;
}

.graph-drop-after::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -2px;
    height: 6px;
    border-radius: 999px;
    background: var(--p-primary-color, #3b82f6);
    pointer-events: none;
    z-index: 3;
}

.workspace-children {
    max-height: 62vh;
    overflow-y: auto;
    padding-right: 0.25rem;
    > :first-child {
        margin-top: 0.2rem;
    }
    > :last-child {
        margin-bottom: 0.2rem;
    }
}

.running-job-indicator {
    margin-left: 0.5rem;
    color: #16a34a;
    font-size: 0.85rem;
}

</style>
